import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CheckCircle, Warning, ArrowCounterClockwise, Printer } from "@phosphor-icons/react";
import { AttendeeCsv } from "@/pages/LaunchPages";
import { QRCodeSVG } from "qrcode.react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { PageTitle, Stat } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";

export function AdminDashboard() {
  const { data: s, loading } = useFetch("/admin/stats");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Mission control" sub="Live status across the tour." />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Revenue (pre-tax)" value={s.revenue_cents == null ? "—" : money(s.revenue_cents)} accent="#1E8E5A" testid="admin-stat-revenue" />
        <Stat label="Paid orders" value={s.orders} testid="admin-stat-orders" />
        <Stat label="Registrations" value={s.registrations} testid="admin-stat-registrations" />
        <Stat label="Checked in" value={s.checked_in} accent="#1E8E5A" testid="admin-stat-checkedin" />
        <Stat label="Students" value={s.students} testid="admin-stat-students" />
        <Stat label="Leads" value={s.leads} testid="admin-stat-leads" />
        <Stat label="Open tickets" value={s.open_tickets} accent="#C8382E" testid="admin-stat-tickets" />
        <Stat label="Tax collected" value={s.tax_cents == null ? "—" : money(s.tax_cents)} testid="admin-stat-tax" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-5">
        <div className="panel p-5 lg:col-span-3">
          <h3 className="font-display text-xl font-bold uppercase">Revenue by city</h3>
          <div className="mt-4 h-64">
            {s.by_city.length === 0 ? <p className="text-sm text-[#475569]">No paid orders yet.</p> : (
              <ResponsiveContainer>
                <BarChart data={s.by_city}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="city" fontSize={12} /><YAxis fontSize={12} /><Tooltip formatter={(v) => `$${v.toLocaleString()}`} /><Bar dataKey="revenue" fill="#E0A21B" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="panel p-5 lg:col-span-2">
          <h3 className="font-display text-xl font-bold uppercase">Next launches</h3>
          <div className="mt-4 space-y-3">
            {s.upcoming.map((e) => (
              <div key={e.id} data-testid={`admin-upcoming-${e.id}`}>
                <div className="flex justify-between text-sm"><span className="font-semibold">{e.city}</span><span className="font-mono-num text-xs">{fmtDate(e.start_at)} · {e.taken}/{e.capacity}</span></div>
                <Progress value={(100 * e.taken) / e.capacity} className="mt-1 h-1.5" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

const EventSelect = ({ value, onChange, tid = "event-select" }) => {
  const { data } = useFetch("/admin/r/events");
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-md border bg-white px-3 text-sm" data-testid={tid}>
      <option value="">All events</option>
      {(data || []).map((e) => <option key={e.id} value={e.id}>{e.title} · {fmtDate(e.start_at)}</option>)}
    </select>
  );
};

export function Checkin() {
  const [eventId, setEventId] = useState("");
  const [code, setCode] = useState("");
  const [last, setLast] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scanner = useRef(null);
  const busy = useRef(false);
  const { data: regs, reload } = useFetch(eventId ? `/admin/registrations?event_id=${eventId}` : null);

  const doCheckin = async (c) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.post("/admin/checkin", { code: c, event_id: eventId || null });
      setLast({ ok: true, ...data });
      if (data.already) toast.warning(`${data.registration.attendee_name} was already checked in`); else toast.success(`Checked in: ${data.registration.attendee_name}`);
      if (eventId) reload();
    } catch (e) {
      setLast({ ok: false, error: errMsg(e) });
      toast.error(errMsg(e));
    } finally {
      setTimeout(() => { busy.current = false; }, 1500);
    }
  };

  const start = async () => {
    try {
      scanner.current = new Html5Qrcode("qr-reader");
      await scanner.current.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (txt) => doCheckin(txt));
      setScanning(true);
    } catch (e) {
      toast.error("Camera not available. Type the ticket code instead.");
    }
  };
  const stop = async () => { try { await scanner.current?.stop(); } catch { /* already stopped */ } setScanning(false); };
  useEffect(() => () => { scanner.current?.stop().catch(() => {}); }, []);

  const undo = async (id) => { await api.post(`/admin/checkin/${id}/undo`); reload(); };
  const ins = (regs || []).filter((r) => r.status === "checked_in").length;

  return (
    <>
      <PageTitle title="Check-in" sub="Scan the ticket QR code or type the code."><EventSelect value={eventId} onChange={setEventId} tid="checkin-event-select" /></PageTitle>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel space-y-4 p-5">
          <div id="qr-reader" className="overflow-hidden rounded-md bg-[#0B1B33]" style={{ minHeight: scanning ? 260 : 0 }} />
          {scanning ? <button className="btn-outline w-full" onClick={stop} data-testid="checkin-stop-scan-btn">Stop camera</button> : <button className="btn-navy w-full" onClick={start} data-testid="checkin-start-scan-btn"><Camera /> Scan with camera</button>}
          <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) { doCheckin(code.trim()); setCode(""); } }} className="flex gap-2">
            <Input placeholder="MCT-XXXXXXXX" value={code} onChange={(e) => setCode(e.target.value)} className="font-mono-num uppercase" data-testid="checkin-code-input" />
            <button className="btn-gold" data-testid="checkin-submit-btn">Check in</button>
          </form>
          {last && (
            <div className={`rounded-md border-l-4 p-4 ${last.ok ? (last.already ? "border-[#E0A21B] bg-[#E0A21B]/10" : "border-[#1E8E5A] bg-[#1E8E5A]/10") : "border-[#C8382E] bg-[#C8382E]/10"}`} data-testid="checkin-result">
              {last.ok ? (
                <>
                  <div className="flex items-center gap-2 font-display text-2xl font-bold uppercase">{last.already ? <Warning className="text-[#E0A21B]" /> : <CheckCircle className="text-[#1E8E5A]" />}{last.registration.attendee_name}</div>
                  <div className="text-sm">{last.registration.tier_name} · {last.registration.event_title}</div>
                  {(last.registration.meal || last.registration.allergies) && <div className="mt-1 text-sm font-semibold">Meal: {last.registration.meal || "–"} · Allergies: {last.registration.allergies || "none"}</div>}
                </>
              ) : <div className="font-semibold text-[#C8382E]">{last.error}</div>}
            </div>
          )}
        </div>
        <div className="panel p-5">
          <div className="flex justify-between"><h3 className="font-display text-xl font-bold uppercase">Roster</h3>{eventId && <span className="font-mono-num text-sm" data-testid="checkin-count">{ins}/{(regs || []).length} in</span>}</div>
          {!eventId ? <p className="mt-3 text-sm text-[#475569]">Choose an event to see the roster.</p> : (
            <div className="mt-3 max-h-[480px] divide-y overflow-y-auto">
              {(regs || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2 text-sm">
                  <div><div className="font-semibold">{r.attendee_name}</div><div className="font-mono-num text-xs text-[#475569]">{r.ticket_code} · {r.tier_name}</div></div>
                  {r.status === "checked_in" ? <button className="flex items-center gap-1 text-xs text-[#1E8E5A]" onClick={() => undo(r.id)} data-testid={`undo-checkin-${r.id}`}><CheckCircle weight="fill" /> In <ArrowCounterClockwise /></button>
                    : r.status === "cancelled" ? <span className="text-xs text-[#C8382E]">Cancelled</span>
                    : <button className="btn-outline px-2 py-1 text-xs" onClick={() => doCheckin(r.ticket_code)} data-testid={`manual-checkin-${r.id}`}>Check in</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const Catering = ({ eventId }) => {
  const { data } = useFetch(`/admin/events/${eventId}/catering`);
  if (!data) return null;
  return (
    <div className="panel mb-3 flex flex-wrap gap-6 p-4 text-sm" data-testid="catering-card">
      <div><div className="text-xs font-bold uppercase tracking-widest text-[#475569]">Lunch count</div><div className="font-mono-num text-2xl" data-testid="lunch-count">{data.lunch_count}</div></div>
      <div><div className="text-xs font-bold uppercase tracking-widest text-[#475569]">By meal</div>{Object.entries(data.meals).map(([m, c]) => <div key={m}>{m}: <span className="font-mono-num">{c}</span></div>)}</div>
      <div><div className="text-xs font-bold uppercase tracking-widest text-[#475569]">Allergies</div>{data.allergies.length === 0 ? "None" : data.allergies.map((a) => <div key={a.name}>{a.name}: {a.allergies}</div>)}</div>
      <Link to={`/superadmin/catering/${eventId}`} className="btn-navy self-center" data-testid="open-lunch-sheet-btn"><Printer /> Lunch order sheet</Link>
      <AttendeeCsv eventId={eventId} />
    </div>
  );
};

const VenueEmails = ({ eventId }) => {
  const { data, reload } = useFetch(`/admin/events/${eventId}/venue-emails`);
  if (!data?.announced_at) return <p className="mb-3 text-xs text-[#475569]" data-testid="venue-emails-none">Venue email: not sent yet. It goes out automatically when you add a venue to this class.</p>;
  const resend = async () => {
    try { const { data: r } = await api.post(`/admin/events/${eventId}/venue-emails/resend`); toast.success(`${r.requeued} email(s) queued again`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel mb-3 flex flex-wrap items-center gap-4 p-4 text-sm" data-testid="venue-emails-status">
      <span className="font-semibold">Venue email ({fmtDateTime(data.announced_at)})</span>
      <span className="text-[#1E8E5A]" data-testid="venue-emails-sent">{data.sent} sent</span>
      <span className="text-[#9A6B00]" data-testid="venue-emails-queued">{data.queued} waiting{data.paused && data.queued ? " (paused by email rate limit, resumes automatically)" : ""}</span>
      <span className="text-[#C8382E]" data-testid="venue-emails-failed">{data.failed} failed</span>
      <button className="text-xs font-semibold underline" onClick={reload} data-testid="venue-emails-refresh">Refresh</button>
      {data.failed > 0 && <button className="btn-outline text-xs" onClick={resend} data-testid="venue-emails-resend-btn">Resend to failed</button>}
    </div>
  );
};

export function AdminRegistrations() {
  const [eventId, setEventId] = useState("");
  const [q, setQ] = useState("");
  const { data, loading } = useFetch(`/admin/registrations?${eventId ? `event_id=${eventId}&` : ""}q=${encodeURIComponent(q)}`);
  const csv = () => {
    const rows = [["Ticket", "Attendee", "Buyer email", "Event", "Tier", "Status", "Meal", "Allergies"], ...(data || []).map((r) => [r.ticket_code, r.attendee_name, r.buyer_email, r.event_title, r.tier_name, r.status, r.meal, r.allergies])];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" }));
    a.download = "registrations.csv";
    a.click();
  };
  return (
    <>
      <PageTitle title="Registrations"><button className="btn-outline" onClick={csv} data-testid="export-registrations-btn">Export CSV</button></PageTitle>
      <div className="mb-3 flex flex-wrap gap-2"><EventSelect value={eventId} onChange={setEventId} /><Input className="max-w-xs bg-white" placeholder="Search name, email, code" value={q} onChange={(e) => setQ(e.target.value)} data-testid="registrations-search-input" /></div>
      {eventId && <Catering eventId={eventId} />}
      {eventId && <VenueEmails eventId={eventId} />}
      {loading ? <Loader /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F6FA] text-left text-xs uppercase tracking-wider text-[#475569]"><tr><th className="p-3">Ticket</th><th className="p-3">Attendee</th><th className="p-3">Buyer</th><th className="p-3">Event</th><th className="p-3">Tier</th><th className="p-3">Status</th><th className="p-3">Meal / allergies</th></tr></thead>
            <tbody>{data.map((r) => (
              <tr key={r.id} className="border-t" data-testid={`admin-reg-row-${r.id}`}>
                <td className="p-3 font-mono-num">{r.ticket_code}</td><td className="p-3 font-semibold">{r.attendee_name}</td><td className="p-3">{r.buyer_email}</td><td className="p-3">{r.event_title}</td><td className="p-3">{r.tier_name}</td>
                <td className="p-3 uppercase">{r.status.replace("_", " ")}{r.cancel_requested && <span className="ml-1 text-[#C8382E]">(cancel req.)</span>}</td><td className="p-3 text-xs">{r.meal || "–"} / {r.allergies || "–"}</td>
              </tr>
            ))}</tbody>
          </table>
          {data.length === 0 && <p className="p-6 text-sm text-[#475569]">No registrations.</p>}
        </div>
      )}
    </>
  );
}

const firstName = (n) => (n || "").trim().split(" ")[0];

export function BadgePrint() {
  const [eventId, setEventId] = useState("");
  const { data, loading } = useFetch(eventId ? `/admin/events/${eventId}/badges` : null);
  return (
    <>
      <div className="no-print"><PageTitle title="Name badge print centre" sub="4 × 3 in badges. Choose an event, then print on badge stock.">
        <div className="flex gap-2"><EventSelect value={eventId} onChange={setEventId} tid="badge-event-select" />{data && <button className="btn-navy" onClick={() => window.print()} data-testid="print-badges-btn"><Printer /> Print</button>}</div>
      </PageTitle></div>
      {!eventId ? <p className="text-sm text-[#475569]">Choose an event.</p> : loading ? <Loader /> : (
        <div className="flex flex-wrap gap-3" data-testid="badge-sheet">
          {data.registrations.map((r) => {
            const tier = data.event.price_tiers.find((t) => t.code === r.tier_code);
            return (
              <div key={r.id} className="flex flex-col overflow-hidden border border-[#0B1B33]/20 bg-white" style={{ width: "4in", height: "3in", breakInside: "avoid" }} data-testid={`name-badge-${r.id}`}>
                <img src="/brand/09_Name_Badge_Strip_4x0.5in.png" alt="" style={{ height: "0.5in", width: "4in", objectFit: "cover" }} />
                <div className="flex flex-1 items-center justify-between px-4">
                  <div className="min-w-0">
                    <div className="truncate font-display text-5xl font-black uppercase leading-none text-[#0B1B33]">{firstName(r.attendee_name)}</div>
                    <div className="mt-1 truncate text-sm">{r.attendee_name}</div>
                    <div className="mt-2 text-xs text-[#475569]">{data.event.title.replace("Mission Control Trading: ", "")} · {fmtDate(data.event.start_at, data.event.city?.timezone)}</div>
                    <div className="text-xs text-[#475569]">Table / seat: ______</div>
                  </div>
                  <QRCodeSVG value={r.ticket_code} size={72} fgColor="#0B1B33" />
                </div>
                <div className="px-4 py-1 text-center text-xs font-bold uppercase tracking-widest text-white" style={{ background: tier?.color || "#0B1B33" }}>{r.tier_name}</div>
              </div>
            );
          })}
          {data.registrations.length === 0 && <p className="text-sm text-[#475569]">No attendees yet.</p>}
        </div>
      )}
    </>
  );
}

export const AdminLink = ({ to, children }) => <Link to={to} className="font-semibold underline">{children}</Link>;
